import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

export function usePermissions() {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: userRoles = [] } = useQuery({
    queryKey: ['userRoles', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      return await base44.entities.UserRole.filter({ user_email: user.email });
    },
    enabled: !!user?.email
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      if (userRoles.length === 0) return [];
      const roleIds = userRoles.map(ur => ur.role_id);
      const allRoles = await base44.entities.Role.list();
      return allRoles.filter(role => roleIds.includes(role.id) && role.is_active);
    },
    enabled: userRoles.length > 0
  });

  // Super admin sempre tem todas as permissões
  const isSuperAdmin = user?.role === 'admin' || permissions?.is_super_admin === true;

  const hasPermission = (entity, action) => {
    if (isSuperAdmin) return true;
    
    return roles.some(role => {
      const entityPerms = role.permissions?.[entity];
      return entityPerms?.[action] === true;
    });
  };

  const canView = (entity) => hasPermission(entity, 'view');
  const canCreate = (entity) => hasPermission(entity, 'create');
  const canEdit = (entity) => hasPermission(entity, 'edit');
  const canDelete = (entity) => hasPermission(entity, 'delete');

  return {
    user,
    permissions,
    userRoles,
    roles,
    isSuperAdmin,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isLoading: !user
  };
}
